import { Control, useController, useFieldArray } from 'react-hook-form';

import {
  IdentityStatusFormValues,
  SenderIdFormValues,
  TrademarkVerificationFormValues,
} from './types';
import {
  createMaxLengthValidator,
  createPatternValidator,
  createRequiredValidator,
  isValidInput,
} from '../../validators';
import regexExpressions from '@/constants/regexExpressions';

export const useIdentityStatusControllers = (
  control: Control<IdentityStatusFormValues, any, IdentityStatusFormValues>
) => {
  const {
    field: { value: appealCategories, onChange: setAppealCategories },
    fieldState: { error: appealCategoriesError },
  } = useController({
    control,
    name: 'appealCategories',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please select Appeal Categories'),
      },
    },
  });

  const {
    field: {
      value: explanation,
      onChange: setExplanation,
      onBlur: validateExplanation,
    },
    fieldState: { error: explanationError },
  } = useController({
    control,
    name: 'explanation',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please enter Explanation'),
        isValidInput,
        withValidMaxLength: createMaxLengthValidator(1024),
      },
    },
  });

  const {
    field: { value: attachmentUuids, onChange: setAttachmentUuids },
    fieldState: { error: attachmentUuidsError },
  } = useController({
    control,
    name: 'attachmentUuids',
  });

  return {
    // Appeal Categories
    appealCategories,
    setAppealCategories,
    appealCategoriesError,
    // Explanation
    explanation,
    setExplanation,
    validateExplanation,
    explanationError,
    // Sample Multimedia Files
    attachmentUuids,
    setAttachmentUuids,
    attachmentUuidsError,
  };
};

export const useTrademarkVerificationControllers = (
  control: Control<
    TrademarkVerificationFormValues,
    any,
    TrademarkVerificationFormValues
  >
) => {
  const {
    field: {
      value: registrationId,
      onChange: setRegistrationId,
      onBlur: validateRegistrationId,
    },
    fieldState: { error: registrationIdError },
  } = useController({
    control,
    name: 'registrationId',
    rules: {
      validate: {
        isRequired: createRequiredValidator(
          'Please enter Trademark Registration Number'
        ),
        withValidPattern: createPatternValidator(
          regexExpressions.registrationId,
          'Trademark Registration Number must contain numbers only'
        ),
        withValidMaxLength: createMaxLengthValidator(100),
      },
    },
  });

  const {
    field: {
      value: trademarkText,
      onChange: setTrademarkText,
      onBlur: validateTrademarkText,
    },
    fieldState: { error: trademarkTextError },
  } = useController({
    control,
    name: 'trademarkText',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please enter Trademark Wording'),
        isValidInput,
        withValidMaxLength: createMaxLengthValidator(1000),
      },
    },
  });

  const {
    field: {
      value: trademarkOwner,
      onChange: setTrademarkOwner,
      onBlur: validateTrademarkOwner,
    },
    fieldState: { error: trademarkOwnerError },
  } = useController({
    control,
    name: 'trademarkOwner',
    rules: {
      validate: {
        isValidInput,
        withValidMaxLength: createMaxLengthValidator(1000, true),
      },
    },
  });

  const {
    field: {
      value: patentOffice,
      onChange: setPatentOffice,
      onBlur: validatePatentOffice,
    },
    fieldState: { error: patentOfficeError },
  } = useController({
    control,
    name: 'patentOffice',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please select Trademark Office'),
      },
    },
  });

  return {
    // Trademark Registration Number
    registrationId,
    setRegistrationId,
    validateRegistrationId,
    registrationIdError,
    // Trademark Wording
    trademarkText,
    setTrademarkText,
    validateTrademarkText,
    trademarkTextError,
    // Trademark Owner
    trademarkOwner,
    setTrademarkOwner,
    validateTrademarkOwner,
    trademarkOwnerError,
    // Trademark Office
    patentOffice,
    setPatentOffice,
    validatePatentOffice,
    patentOfficeError,
  };
};

export const useSenderIdControllers = (
  control: Control<SenderIdFormValues, any, SenderIdFormValues>
) => {
  const {
    fields: senderIds,
    append: addSenderId,
    remove: removeSenderId,
  } = useFieldArray({
    control,
    name: 'senderIds',
  });

  return {
    senderIds,
    addSenderId,
    removeSenderId,
  };
};
